import { useMemo } from 'react';
import { Box, Paper, Stack, Typography, Chip, Alert, Link } from '@mui/material';
import Section from '../components/Section';
import DataTable from '../components/DataTable';
import useResource from '../components/useResource';

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
};

// End-date colour coding: red < 30 days, amber < 90 days, green otherwise.
const endDateColor = (iso) => {
  const days = daysUntil(iso);
  if (days === null) return 'default';
  if (days < 30) return 'error';
  if (days < 90) return 'warning';
  return 'success';
};

const utilPct = (consumed, total) => {
  const c = Number(consumed);
  const t = Number(total);
  if (!t || isNaN(t) || isNaN(c)) return null;
  return Math.round((c / t) * 100);
};

// Utilization colour coding: red >= 100%, amber >= 90%, green otherwise.
const utilColor = (pct) => (pct == null ? 'default' : pct >= 100 ? 'error' : pct >= 90 ? 'warning' : 'success');

function Field({ label, children }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{children || '—'}</Typography>
    </Stack>
  );
}

/**
 * Overview tab — business profile, contacts, active contracts and subscriptions
 * for one account. Contracts are loaded by the page shell (shared with the
 * header stats) and passed in; the rest load independently here.
 */
export default function OverviewTab({ code, account, company, contracts, refreshKey }) {
  const contacts   = useResource(`autotask/contact?account=${encodeURIComponent(code)}`, { refreshKey });
  const subs       = useResource(`csp/customers/${encodeURIComponent(code)}/subscribedskus`, { refreshKey });

  const companyRec = Array.isArray(company?.data)
    ? (company.data.find(c => String(c.id) === String(account?.autotask_id)) || company.data[0])
    : null;

  const address = companyRec
    ? [companyRec.address1, companyRec.address2, [companyRec.city, companyRec.state].filter(Boolean).join(', '), companyRec.postalCode]
        .filter(Boolean).join(' · ')
    : '';

  // Show only real, current people: drop inactive contacts and tenant
  // *.onmicrosoft.com mailboxes (service/role accounts, not actual contacts).
  const contactRows = (Array.isArray(contacts.data) ? contacts.data : []).filter(c => {
    if (c.isActive === 0) return false;
    if ((c.emailAddress || '').toLowerCase().endsWith('.onmicrosoft.com')) return false;
    return true;
  });
  const contractRows = Array.isArray(contracts?.data) ? contracts.data : [];
  const subRows      = Array.isArray(subs.data) ? subs.data : [];

  // A child account (asl.gln) has no M365 tenant of its own — it shares its parent's, and
  // cma-api resolves the code to that parent's tenant_id. So these subscriptions are the
  // PARENT's, tenant-wide, not this location's. Label them: an unqualified seat count here
  // reads as belonging to this location alone, which is the exact confusion the per-location
  // audit work exists to remove.
  const parentCode = code && code.includes('.') ? code.slice(0, code.lastIndexOf('.')) : null;

  const contactColumns = useMemo(() => [
    {
      id: 'name', header: 'Name',
      accessorFn: r => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '(unnamed)',
      Cell: ({ row }) => {
        const r = row.original;
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`${r.firstName || ''} ${r.lastName || ''}`.trim() || '(unnamed)'}
            </Typography>
            {r.primaryContact === 1 && <Chip size="small" color="primary" label="Primary" sx={{ height: 18 }} />}
          </Stack>
        );
      },
    },
    { accessorKey: 'title', header: 'Title' },
    { accessorKey: 'emailAddress', header: 'Email' },
    { id: 'phone', header: 'Phone', accessorFn: r => r.phone || r.mobilePhone || '—' },
  ], []);

  const contractColumns = useMemo(() => [
    { accessorKey: 'contractName', header: 'Contract' },
    { id: 'start', header: 'Start', accessorFn: r => fmtDate(r.startDate) },
    {
      id: 'end', header: 'End',
      accessorFn: r => r.endDate || '',
      Cell: ({ row }) => {
        const iso = row.original.endDate;
        return <Chip size="small" color={endDateColor(iso)} variant="outlined" label={fmtDate(iso)} />;
      },
    },
    { id: 'status', header: 'Status', Cell: () => <Chip size="small" color="success" variant="outlined" label="Active" /> },
  ], []);

  const subColumns = useMemo(() => [
    { id: 'product', header: 'Product', accessorFn: r => r.productSku?.name || r.productSku?.skuPartNumber || '—' },
    { id: 'purchased', header: 'Purchased', accessorFn: r => r.totalUnits ?? r.activeUnits ?? '—' },
    { id: 'consumed', header: 'Consumed', accessorFn: r => r.consumedUnits ?? '—' },
    {
      id: 'util', header: 'Utilization',
      accessorFn: r => utilPct(r.consumedUnits, r.totalUnits ?? r.activeUnits) ?? -1,
      Cell: ({ row }) => {
        const r = row.original;
        const pct = utilPct(r.consumedUnits, r.totalUnits ?? r.activeUnits);
        return pct == null
          ? <Typography variant="body2" color="text.secondary">—</Typography>
          : <Chip size="small" color={utilColor(pct)} variant="outlined" label={`${pct}%`} />;
      },
    },
  ], []);

  return (
    <Box>
      {/* Company profile */}
      <Section title="Business Profile" loading={company?.loading} error={company?.error}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Field label="Address">{address}</Field>
              <Field label="Phone">{companyRec?.phone}</Field>
              <Field label="Website">
                {companyRec?.webAddress
                  ? <Link href={/^https?:\/\//.test(companyRec.webAddress) ? companyRec.webAddress : `https://${companyRec.webAddress}`}
                      target="_blank" rel="noopener noreferrer">{companyRec.webAddress}</Link>
                  : '—'}
              </Field>
            </Box>
            <Box>
              <Field label="Account classification">{companyRec?.classificationName}</Field>
              <Field label="Account manager">{companyRec?.ownerResourceName}</Field>
              <Field label="CMA account code">{code}</Field>
            </Box>
          </Box>
        </Paper>
      </Section>

      {/* Contacts */}
      <Section title="Contacts" count={contactRows.length} loading={contacts.loading} error={contacts.error}>
        {!contacts.loading && !contacts.error && contactRows.length === 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>No contacts on file — this is a knowledge gap.</Alert>
        )}
        {contactRows.length > 0 && (
          <DataTable
            columns={contactColumns}
            data={contactRows}
            isLoading={contacts.loading}
            emptyText="No contacts on file."
            enableGlobalFilter
            enableTopToolbar
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            positionGlobalFilter="left"
            initialState={{ showGlobalFilter: true }}
            muiSearchTextFieldProps={{
              placeholder: 'Search name, title, email, or phone',
              size: 'small',
              variant: 'outlined',
              sx: { minWidth: 320 },
            }}
          />
        )}
      </Section>

      {/* Contracts */}
      <Section title="Active Contracts" count={contractRows.length} loading={contracts?.loading} error={contracts?.error}>
        <DataTable columns={contractColumns} data={contractRows} isLoading={contracts?.loading} emptyText="No active contracts." />
      </Section>

      {/* Subscriptions — tenant-wide, and shared with the parent on a child account */}
      <Section
        title={parentCode ? 'Tenant Subscriptions' : 'Subscriptions'}
        count={subRows.length}
        loading={subs.loading}
        error={subs.error}
        action={parentCode && (
          <Chip
            size="small"
            variant="outlined"
            label={`shared tenant · ${parentCode}`}
            title={`This location has no M365 tenant of its own. These are ${parentCode}'s tenant-wide subscriptions, covering every location under it — not seats assigned to this one.`}
          />
        )}
      >
        <DataTable columns={subColumns} data={subRows} isLoading={subs.loading} emptyText="No subscriptions found." />
      </Section>
    </Box>
  );
}
